import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_TOQUES, MAX_HORAS_UI, formularioDesdeDocumento, documentoDesdeFormulario,
  validarFormulario, resolverZonaInicial,
} from '../../utils/seguimientoAutoConfig';
import { SELECTABLE_TIMEZONES, getTimezoneFromCountry } from '../../utils/timezoneMap';

/**
 * Configuración del SEGUIMIENTO AUTOMÁTICO por bot (docs/SEGUIMIENTO.md §2.2).
 *
 * Copia del patrón de `BookingRemindersConfig` (CalendarIntegration.jsx): estado local, un
 * `onUpdate` del panel que escribe en `bots/{botId}/integrations/config` con merge, y textos
 * en `integrations.seguimientoAuto.*`.
 *
 * LO QUE NO DECIDE ESTE COMPONENTE: ni los valores por defecto, ni los topes, ni la forma
 * del documento. Todo eso sale de `utils/seguimientoAutoConfig` (copia byte-idéntica de la
 * del backend), y por eso lo que se guarda aquí es exactamente lo que el barrido lee.
 *
 * LA ZONA HORARIA: al abrir sin config, se resuelve con la cadena del tenant (`timeZone` →
 * país → Chile), se muestra y se guarda. A partir de ahí manda lo guardado (C5).
 */
export default function SeguimientoAutoConfig({ config, onUpdate, context }) {
  const { t } = useTranslation('bots');
  const zonaInicial = resolverZonaInicial(context?.organization, getTimezoneFromCountry);
  const [form, setForm] = useState(() => formularioDesdeDocumento(config?.seguimientoAuto ?? null, zonaInicial));
  const [saving, setSaving] = useState(false);
  const [errores, setErrores] = useState([]);

  useEffect(() => {
    setForm(formularioDesdeDocumento(config?.seguimientoAuto ?? null, zonaInicial));
    // La zona inicial sólo cambia si cambia la organización; `config` es la señal de recarga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.seguimientoAuto]);

  const cambiar = (parcial) => { setErrores([]); setForm((f) => ({ ...f, ...parcial })); };

  const cambiarToques = (n) => {
    const toques = Number(n);
    const horas = form.horas.slice(0, toques);
    // Un toque nuevo entra por detrás con una hora mayor que la última, sin pasar del tope.
    while (horas.length < toques) {
      const ultima = horas.length ? horas[horas.length - 1] : 0;
      horas.push(Math.min(ultima + 4, MAX_HORAS_UI));
    }
    cambiar({ toques, horas });
  };

  const cambiarHora = (i, valor) => {
    const horas = [...form.horas];
    horas[i] = valor === '' ? '' : Number(valor);
    cambiar({ horas });
  };

  const handleSave = async () => {
    const errs = validarFormulario(form);
    if (errs.length) { setErrores(errs); return; }
    setSaving(true);
    try {
      await onUpdate({ seguimientoAuto: documentoDesdeFormulario(form) });
      alert(t('integrations.seguimientoAuto.saved'));
    } catch (error) {
      console.error('Error saving seguimientoAuto:', error);
      alert(t('integrations.seguimientoAuto.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const zonas = SELECTABLE_TIMEZONES.some((z) => z.value === form.timeZone)
    ? SELECTABLE_TIMEZONES
    : [{ value: form.timeZone, label: form.timeZone }, ...SELECTABLE_TIMEZONES];

  const input = { padding: '0.4rem 0.5rem', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.9rem' };

  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1.5rem', marginTop: '1rem' }}>
      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>{t('integrations.seguimientoAuto.title')}</h3>
      <p style={{ color: '#718096', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>{t('integrations.seguimientoAuto.desc')}</p>

      <div style={{ margin: '1rem 0', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.activado} onChange={(e) => cambiar({ activado: e.target.checked })} style={{ width: '18px', height: '18px' }} />
          <span>{t('integrations.seguimientoAuto.enable')}</span>
        </label>
        <p style={{ color: '#718096', fontSize: '0.8rem', margin: '0.4rem 0 0 1.6rem' }}>{t('integrations.seguimientoAuto.enableHint')}</p>
        {/* Saludos (bloque 8): activado por defecto; sólo un false explícito lo apaga. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.75rem' }}>
          <input type="checkbox" checked={form.seguimientoASaludos !== false} onChange={(e) => cambiar({ seguimientoASaludos: e.target.checked })} style={{ width: '18px', height: '18px' }} />
          <span>{t('integrations.seguimientoAuto.saludos')}</span>
        </label>
        <p style={{ color: '#718096', fontSize: '0.8rem', margin: '0.4rem 0 0 1.6rem' }}>{t('integrations.seguimientoAuto.saludosHint')}</p>
      </div>

      <div style={{ margin: '1.25rem 0' }}>
        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', fontWeight: 600 }}>{t('integrations.seguimientoAuto.toquesTitle')}</h4>
        <select value={form.toques} onChange={(e) => cambiarToques(e.target.value)} style={input} aria-label={t('integrations.seguimientoAuto.toquesTitle')}>
          {Array.from({ length: MAX_TOQUES }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{t('integrations.seguimientoAuto.toquesOpcion', { count: n })}</option>
          ))}
        </select>
      </div>

      <div style={{ margin: '1.25rem 0' }}>
        <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.95rem', fontWeight: 600 }}>{t('integrations.seguimientoAuto.horasTitle')}</h4>
        <p style={{ color: '#718096', fontSize: '0.8rem', margin: '0 0 0.75rem 0' }}>{t('integrations.seguimientoAuto.horasDesc', { max: MAX_HORAS_UI })}</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {form.horas.map((h, i) => (
            <label key={i} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
              <span>{t('integrations.seguimientoAuto.toqueN', { n: i + 1 })}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <input type="number" min={1} max={MAX_HORAS_UI} step={1} value={h} onChange={(e) => cambiarHora(i, e.target.value)} style={{ ...input, width: '5rem' }} />
                <span>{t('integrations.seguimientoAuto.horasSufijo')}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div style={{ margin: '1.25rem 0' }}>
        <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.95rem', fontWeight: 600 }}>{t('integrations.seguimientoAuto.horarioTitle')}</h4>
        <p style={{ color: '#718096', fontSize: '0.8rem', margin: '0 0 0.75rem 0' }}>{t('integrations.seguimientoAuto.horarioDesc')}</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span>{t('integrations.seguimientoAuto.desde')}</span>
            <input type="time" value={form.horarioPermitido.desde} onChange={(e) => cambiar({ horarioPermitido: { ...form.horarioPermitido, desde: e.target.value } })} style={input} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span>{t('integrations.seguimientoAuto.hasta')}</span>
            <input type="time" value={form.horarioPermitido.hasta} onChange={(e) => cambiar({ horarioPermitido: { ...form.horarioPermitido, hasta: e.target.value } })} style={input} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem', minWidth: '14rem' }}>
            <span>{t('integrations.seguimientoAuto.zona')}</span>
            <select value={form.timeZone} onChange={(e) => cambiar({ timeZone: e.target.value })} style={input}>
              {zonas.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {errores.length > 0 && (
        <ul role="alert" style={{ color: '#c53030', fontSize: '0.85rem', margin: '0.5rem 0 1rem 0', paddingLeft: '1.2rem' }}>
          {errores.map((codigo) => <li key={codigo}>{t(`integrations.seguimientoAuto.errores.${codigo}`, { max: MAX_HORAS_UI })}</li>)}
        </ul>
      )}

      <button onClick={handleSave} disabled={saving} style={{ padding: '0.6rem 1.2rem', background: saving ? '#a0aec0' : '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
        {saving ? t('integrations.saving') : t('integrations.seguimientoAuto.save')}
      </button>
    </div>
  );
}
